import React, { useState } from 'react';
import './CombineSidebar.css';
import {
  Box,
  Slider,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
  Switch,
} from '@mui/material';
import { FiX } from 'react-icons/fi';
import { findConsecutiveTrips } from '../services/tripService';

function CombineSidebar({ isOpen, onClose, onTripsFound }) {
  // State for form values
  const [numRallies, setNumRallies] = useState(2);
  const [maxPauseDays, setMaxPauseDays] = useState(3);
  const [maxOverlapDays, setMaxOverlapDays] = useState(0);
  const [returnToStart, setReturnToStart] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [error, setError] = useState(null);
  const [enableNearby, setEnableNearby] = useState(false);
  const [radiusKm, setRadiusKm] = useState(50);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const result = await findConsecutiveTrips({
        numRallies,
        maxPauseDays,
        maxOverlapDays,
        returnToStart,
        enableNearby,
        radiusKm
      });
      
      onTripsFound(result.consecutive_trips);
      setShowSnackbar(true);
      
      // Close sidebar on mobile after submitting with 1s delay
      if (window.innerWidth <= 768) {
        setTimeout(() => {
          onClose();
        }, 1000);
      }
    } catch (err) {
      setError(err.message);
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`combine-sidebar ${isOpen ? 'open' : ''}`}>
      <div className="combine-header">
        <h3>Kombinieren</h3>
        <button className="close-button" onClick={onClose}>
          <FiX />
        </button>
      </div>
      
      <div className="combine-content">
        <Box component="form" onSubmit={handleSubmit}>
          {/* Number of Rallies */}
          <div className="combine-section">
            <Typography variant="subtitle2" gutterBottom>Anzahl Ralleys</Typography>
            <Typography variant="caption" gutterBottom>Wie vieeeele Ralleys?</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
              <Slider
                value={numRallies}
                onChange={(_, value) => setNumRallies(value)}
                min={2}
                max={5}
                marks
                size="small"
                valueLabelDisplay="auto"
                sx={{ flex: 1 }}
              />
              <TextField
                value={numRallies}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 2;
                  setNumRallies(value);
                }}
                type="number"
                size="small"
                inputProps={{ min: 2 }}
                sx={{ width: 70 }}
              />
            </Box>
          </div>

          {/* Max Pause Days */}
          <div className="combine-section">
            <Typography variant="subtitle2" gutterBottom>Max Pausentage</Typography>
            <Typography variant="caption" gutterBottom>Wie lange warten zwichen Ralleys?</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Slider
                value={maxPauseDays}
                onChange={(_, value) => setMaxPauseDays(value)}
                min={0}
                max={14}
                marks
                size="small"
                valueLabelDisplay="auto"
                sx={{ flex: 1 }}
              />
              <TextField
                value={maxPauseDays}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 0;
                  setMaxPauseDays(Math.min(999, Math.max(0, value)));
                }}
                type="number"
                size="small"
                inputProps={{ min: 0, max: 999 }}
                sx={{ width: 70 }}
              />
            </Box>
          </div>

          {/* Max Overlap Days */}
          <div className="combine-section">
            <Typography variant="subtitle2" gutterBottom>Max Tage Überlappung</Typography>
            <Typography variant="caption" gutterBottom>Wie viel Tage früher kannst du eine Rallye starten um die Anschluss-rallye zu packen?</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Slider
                value={maxOverlapDays}
                onChange={(_, value) => setMaxOverlapDays(value)}
                min={0}
                max={7}
                marks
                size="small"
                valueLabelDisplay="auto"
                sx={{ flex: 1 }}
              />
              <TextField
                value={maxOverlapDays}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 0;
                  setMaxOverlapDays(Math.min(7, Math.max(0, value)));
                }}
                type="number"
                size="small"
                inputProps={{ min: 0, max: 7 }}
                sx={{ width: 70 }}
              />
            </Box>
          </div>

          {/* Add new section before the Return to Start checkbox */}
          <div className="combine-section nearby-stations-section">
            <Box sx={{ mb: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={enableNearby}
                    onChange={(e) => setEnableNearby(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="subtitle2">Kombinierte Stationen</Typography>}
              />
            </Box>
            
            {enableNearby && (
              <>
                <Typography variant="caption" display="block" gutterBottom sx={{ mb: 2 }}>
                  z.B. Rückgabe bei Station A, Weiterfahrt bei Station B, falls B innerhalb X m von A ist.
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Slider
                    value={radiusKm}
                    onChange={(_, value) => setRadiusKm(value)}
                    min={10}
                    max={150}
                    marks
                    size="small"
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value}km`}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    value={radiusKm}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 10;
                      setRadiusKm(Math.min(150, Math.max(10, value)));
                    }}
                    type="number"
                    size="small"
                    inputProps={{ min: 10, max: 150 }}
                    sx={{ width: 70 }}
                  />
                </Box>
              </>
            )}
          </div>

          {/* Return to Start Checkbox */}
          <div className="combine-section">
            <FormControlLabel
              control={
                <Checkbox
                  checked={returnToStart}
                  onChange={(e) => setReturnToStart(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="subtitle2">Zurück zum Start</Typography>}
            />
          </div>

          <Button
            variant="contained"
            type="submit"
            disabled={loading}
            fullWidth
            sx={{ 
              mt: 'auto',
              backgroundColor: '#0d6efd',
              '&:hover': {
                backgroundColor: '#0b5ed7'
              }
            }}
          >
            {loading ? <CircularProgress size={20} /> : "LETS GOOO"}
          </Button>
        </Box>
      </div>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        message={error || "Erfolgreich kombiniert!"}
        sx={{ 
          '& .MuiSnackbarContent-root': {
            backgroundColor: error ? '#d32f2f' : '#43a047'
          }
        }}
      />
    </div>
  );
}

export default CombineSidebar; 